'use client';

import {cva} from 'class-variance-authority';
import * as React from 'react';

import {cn} from '../../lib/utils';

import type {VariantProps} from 'class-variance-authority';

export const alertVariantsConfig = {
  variants: {
    variant: {
      default: 'bg-background-surface text-fg',
      destructive:
        'bg-background-surface text-destructive *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-current'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
} as const;

const alertVariants = cva(
  "animate-in fade-in slide-in-from-top-1 duration-200 group/alert relative grid w-full gap-0.5 rounded-lg border px-4 py-3 text-start text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pe-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  alertVariantsConfig
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({variant}), className)}
      {...props}
    />
  );
}

function AlertTitle({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-fg',
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm text-balance text-fg-tertiary md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-fg [&_p:not(:last-child)]:mb-4',
        className
      )}
      {...props}
    />
  );
}

function AlertAction({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div data-slot="alert-action" className={cn('absolute top-2.5 end-3', className)} {...props} />
  );
}

export {Alert, AlertTitle, AlertDescription, AlertAction};
